import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TRecordPayoutData, TTradeDetailsById } from "../types/trades/trades";
import moment from "moment";

export type TAddPaymentDetails = {
  dueDate: string;
  arlDate: string;
  pmtTerm: string;
  advanceLimit: number;
  balPayable: number;
  delayedDays: string;
  delayedChargesPer: number;
  delayedChargesVal: number;
};

type TInitialState = {
  paymentUpdateStatus: boolean;
  payoutCurrentStep: number;
  recordPayoutData: TRecordPayoutData;
  selectedPaymentType: string;
  addSrcPmtDetail: TAddPaymentDetails;
  addDestPmtDetail: TAddPaymentDetails;
  editAdvancePayment: boolean;
  closedTradeTraderType: string;
  closedTradeSubmitDisable: boolean;
};

const initialState: TInitialState = {
  paymentUpdateStatus: false,
  payoutCurrentStep: 1,
  editAdvancePayment: false,
  recordPayoutData: {
    partyId: "",
    paymentDate: "",
    paidAmount: "",
    paymentType: "",
    paymentMode: "",
    comments: "",
    paymentTypeVal: "",
    requestAdv: 0,
    pmtReceiptDate: "",
    desc: "",
    utr: "",
    inOutWard: "",
    fundAccId: "",
    accountNumber: "",
    otpValidated: false,
    otp: "",
    refId: "",
    id: 0,
    availableBalance: 0,
    loanAmt: 0,
  },
  selectedPaymentType: "",
  addSrcPmtDetail: {
    dueDate: "",
    arlDate: "",
    pmtTerm: "",
    advanceLimit: 0,
    balPayable: 0,
    delayedDays: "",
    delayedChargesPer: 0,
    delayedChargesVal: 0,
  },
  addDestPmtDetail: {
    dueDate: "",
    arlDate: "",
    pmtTerm: "",
    advanceLimit: 0,
    balPayable: 0,
    delayedDays: "",
    delayedChargesPer: 0,
    delayedChargesVal: 0,
  },
  closedTradeTraderType: "",
  closedTradeSubmitDisable: false,
};

export const paymentDetailSlice = createSlice({
  name: "paymentDetails",
  initialState: initialState,
  reducers: {
    resetTradeDetails: (state) => {
      return {
        ...initialState,
      };
    },

    updateSelectedPaymentType: (state, action: PayloadAction<string>) => {
      state.selectedPaymentType = action.payload;
    },
    setRecordPayoutData: (
      state,
      action: PayloadAction<{
        partyId: number;
        inOutWard: string;
        accountNumber: string;
        fundAccId: string;
      }>
    ) => {
      state.recordPayoutData = {
        partyId: action.payload.partyId,
        paymentDate: moment().format("YYYY-MM-DD"),
        paidAmount: "",
        paymentType: "",
        paymentMode: "",
        comments: "",
        paymentTypeVal: "",
        requestAdv: 0,
        pmtReceiptDate: "",
        desc: "",
        utr: "",
        inOutWard: action.payload.inOutWard,
        accountNumber: action.payload.accountNumber,
        fundAccId: action.payload.fundAccId,
        otpValidated: false,
        otp: "",
        refId: "",
        id: 0,
        availableBalance: 0,
        loanAmt: 0,
      };
      state.payoutCurrentStep = 1;
    },
    updateRecordPayoutData: (state, action) => {
      state.recordPayoutData = action.payload;
    },

    setAddSrcPmt: (
      state,
      action: PayloadAction<{
        tradeDetail: TTradeDetailsById;
      }>
    ) => {
      let tradeDetail = action.payload.tradeDetail;
      const addDays = (date: any, days: any) => {
        var dateVal = new Date(date);
        var result = "";
        dateVal.setDate(dateVal.getDate() + parseInt(days));
        result = moment(dateVal).format("DD-MMM-YYYY");
        return result;
      };
      state.addSrcPmtDetail.arlDate = tradeDetail?.arlDate
        ? tradeDetail?.arlDate
        : moment(new Date()).format("YYYY-MM-DD");
      state.addSrcPmtDetail.pmtTerm = tradeDetail?.srcPmtTerm;
      state.addSrcPmtDetail.dueDate =
        (tradeDetail?.arlDate &&
          tradeDetail?.srcPmtTerm &&
          addDays(tradeDetail?.arlDate, tradeDetail?.srcPmtTerm || "7")) ||
        moment(new Date()).format("YYYY-MM-DD");
      state.addSrcPmtDetail.advanceLimit = Number(
        tradeDetail?.reqAdv?.advLimit
      );
    },

    setAddDestPmt: (
      state,
      action: PayloadAction<{
        tradeDetail: TTradeDetailsById;
      }>
    ) => {
      let tradeDetail = action.payload.tradeDetail;
      const addDays = (date: any, days: any) => {
        var dateVal = new Date(date);
        var result = "";
        dateVal.setDate(dateVal.getDate() + parseInt(days));
        result = moment(dateVal).format("DD-MMM-YYYY");
        return result;
      };
      const getDaysDelayed = (dueDate: string): number => {
        const today = moment();
        const due = moment(dueDate, "YYYY-MM-DD");
        const delay = today.diff(due, "days");
        return delay > 0 ? delay : 0;
      };
      state.addDestPmtDetail.arlDate = tradeDetail?.arlDate
        ? tradeDetail?.arlDate
        : moment(new Date()).format("YYYY-MM-DD");
      state.addDestPmtDetail.pmtTerm = tradeDetail?.destPmtTerm;
      state.addDestPmtDetail.dueDate =
        (tradeDetail?.arlDate &&
          tradeDetail?.destPmtTerm &&
          addDays(tradeDetail?.arlDate, tradeDetail?.destPmtTerm || "7")) ||
        moment(new Date()).format("YYYY-MM-DD");
      state.addDestPmtDetail.advanceLimit = Number(
        tradeDetail?.reqAdv?.advLimit
      );
      state.addDestPmtDetail.balPayable = tradeDetail?.balPayable;
      state.addDestPmtDetail.delayedDays = getDaysDelayed(
        tradeDetail?.dueDate
      ).toString();
      state.addDestPmtDetail.delayedChargesPer =
        (Number(tradeDetail?.delayedChargesVal) * 100) /
        Number(tradeDetail?.pattiAmt);
      state.addDestPmtDetail.delayedChargesVal = Number(
        tradeDetail?.delayedChargesVal
      );
    },

    udpateAddSrcPmt: (
      state,
      action: PayloadAction<{
        srcPmt: TAddPaymentDetails;
      }>
    ) => {
      state.addSrcPmtDetail = action.payload.srcPmt;
    },
    udpateAddDestPmt: (
      state,
      action: PayloadAction<{
        destPmt: TAddPaymentDetails;
      }>
    ) => {
      state.addDestPmtDetail = action.payload.destPmt;
    },

    updateEditAdvPayment: (
      state,
      action: PayloadAction<{
        editAdvancePayment: boolean;
      }>
    ) => {
      state.editAdvancePayment = action.payload.editAdvancePayment;
    },

    setPayoutCurrentStep: (state, action) => {
      state.payoutCurrentStep = action.payload;
    },

    setClosedTradeTraderType: (state, action: PayloadAction<string>) => {
      state.closedTradeTraderType = action.payload;
    },
    setClosedSubmitBtnDisableStatus: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.closedTradeSubmitDisable = action.payload;
    },
    resetClosedSubmitBtnDisableStatus: (state) => {
      state.closedTradeSubmitDisable = false;
    },
  },
});

export const {
  updateRecordPayoutData,
  setRecordPayoutData,
  updateSelectedPaymentType,
  udpateAddSrcPmt,
  udpateAddDestPmt,
  setAddSrcPmt,
  setAddDestPmt,
  setPayoutCurrentStep,
  setClosedTradeTraderType,
  setClosedSubmitBtnDisableStatus,
  resetClosedSubmitBtnDisableStatus,
} = paymentDetailSlice.actions;
export default paymentDetailSlice.reducer;
